"use client";

import { useEffect, useState, type RefObject } from "react";
import type { ConverseUiState } from "@/components/composer/useConverse";
import { useComposerDotsStore } from "@/stores/composerDotsStore";
import { flyTaskToRecents, scrollRecentsToTop } from "./taskFlight";

/** The task receipt's choreography (founder 09-01: "show it there, then bring
 *  Bash back in the cockpit and animate the task going up"): the receipt
 *  shows on Bash's row, Bash flies home, the task lifts up and away, the
 *  thread folds and the task's dot lands on the rim. */
export const RECEIPT_SHOW_MS = 1400;

export type ReceiptStage = "show" | "lift" | null;

/**
 * A task receipt: shown on Bash's row, then Bash flies home, then the task
 * lifts up and away and the thread folds, stamping the rim dot on the way.
 * Moved out of BashPill verbatim; the stage is its only output.
 */
export function usePillReceipt(
  ui: ConverseUiState,
  dismiss: () => void,
  rootRef: RefObject<HTMLElement | null>
): ReceiptStage {
  const receipt =
    ui.phase === "routed" &&
    (ui.action.kind === "task_created" || ui.action.kind === "task_followup")
      ? ui.action
      : null;
  const receiptTaskId = receipt?.taskId ?? null;
  const [stage, setStage] = useState<ReceiptStage>(null);
  useEffect(() => {
    if (!receipt) {
      setStage(null);
      return;
    }
    const dot = {
      taskId: receipt.taskId,
      title: receipt.title,
      model: receipt.kind === "task_created" ? receipt.model : undefined,
    };
    setStage("show");
    // Bring the top of Recents into view while the receipt shows, so a task
    // created from a scrolled-down list still has a visible slot to fly to
    // (founder 09-03). RECEIPT_SHOW_MS lets the smooth scroll settle before the
    // flight measures the landing row.
    scrollRecentsToTop();
    // Then, in the same moment (founder 09-01: "same time"): Bash flies home
    // and the row hands itself to the flight (taskFlight.ts), a card that
    // travels to the task's Recents row and lands. The thread folds only
    // then, and the rim dot is stamped on the way.
    let cancelled = false;
    const t1 = window.setTimeout(() => {
      setStage("lift");
      const row =
        rootRef.current?.querySelector<HTMLElement>(".bh-brow--receipt") ??
        null;
      void flyTaskToRecents(row, receipt.taskId).then(() => {
        if (cancelled) return;
        useComposerDotsStore.getState().add(dot);
        dismiss();
      });
    }, RECEIPT_SHOW_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(t1);
    };
    // Keyed on the task id: `receipt` is re-derived each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiptTaskId, dismiss]);
  return stage;
}
